import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Dialog,
    DialogContent,
    Snackbar,
    TextField,
    Typography,
} from '@mui/material';
import { MainContent } from '../../components/MainContent';
import { IStoredFileDto, Order, SortBy } from '../../clients/services';
import { FileLibraryService } from '../../services';
import { actionContainerStyle } from '../Tickets/styles';
import { FileLibraryListItem, ReactMediaLibrary } from 'react-media-library';
import { useAuth } from '../../auth/auth-provider';
import './Files.scss';

const fileLibraryService = new FileLibraryService();
const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/avif'];
const serviceUrl = (process.env.REACT_APP_SERVICE_URL as string | undefined)?.replace(/\/$/, '') ?? '';
const acceptedLibraryTypes = [
    ...imageTypes,
    'application/pdf',
    'video/mp4',
    'video/webm',
    'video/quicktime',
    'audio/mpeg',
    'audio/wav',
    'audio/ogg',
];

const formatBytes = (size?: number): string => {
    if (!size || size <= 0) {
        return '0 B';
    }
    if (size < 1024) {
        return `${size} B`;
    }

    const kb = size / 1024;
    if (kb < 1024) {
        return `${kb.toFixed(1)} KB`;
    }

    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
};

const formatDate = (value?: string): string => {
    if (!value) {
        return '-';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return parsed.toLocaleString('de-CH');
};

export const Files: React.FC = () => {
    const { profile, hasRole } = useAuth();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [allLibraryFiles, setAllLibraryFiles] = useState<IStoredFileDto[]>([]);
    const [files, setFiles] = useState<IStoredFileDto[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [selectedImage, setSelectedImage] = useState<IStoredFileDto | null>(null);
    const [isImagePreviewOpen, setIsImagePreviewOpen] = useState<boolean>(false);
    const [isLibraryOpen, setIsLibraryOpen] = useState<boolean>(false);
    const [importSuccessMessage, setImportSuccessMessage] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState<string>('');

    const isAdmin = hasRole(profile, 'admin');

    const normalizeSearchText = (value?: string): string => {
        return (value ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();
    };

    const filterFilesByQuery = (source: IStoredFileDto[], query: string): IStoredFileDto[] => {
        const normalizedQuery = normalizeSearchText(query).trim();
        if (!normalizedQuery) {
            return source;
        }

        return source.filter((file) => {
            const haystack = normalizeSearchText(
                [file.fileName, file.originalName, file.title ?? '', file.comment ?? '', file.category ?? ''].join(' '),
            );
            return haystack.includes(normalizedQuery);
        });
    };

    const isPublicRepoFile = (file: IStoredFileDto): boolean => {
        return file.id.startsWith('public:') || file.category === 'public';
    };

    const getFileDisplayPath = (file: IStoredFileDto): string => {
        const rawUrl = file.publicUrl ?? '';

        let normalizedPath = rawUrl;
        if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
            try {
                normalizedPath = new URL(rawUrl).pathname;
            } catch {
                normalizedPath = rawUrl;
            }
        }

        if (normalizedPath.startsWith('/library/')) {
            return normalizedPath.slice(1);
        }

        if (normalizedPath.startsWith('/files/')) {
            return `library/${file.fileName}`;
        }

        if (normalizedPath.startsWith('library/')) {
            return normalizedPath;
        }

        if (normalizedPath.startsWith('files/')) {
            return `library/${file.fileName}`;
        }

        return file.fileName;
    };

    const isManagedLibraryFile = (file: IStoredFileDto): boolean => {
        return getFileDisplayPath(file).startsWith('library/');
    };

    const resolveFilePublicUrl = (file: IStoredFileDto): string => {
        const rawUrl = file.publicUrl ?? '';
        if (!rawUrl) {
            return '';
        }

        if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
            return rawUrl;
        }

        const normalizedPath = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
        const isManagedLibraryPath = normalizedPath.startsWith('/library/') || normalizedPath.startsWith('/files/');
        if (!isManagedLibraryPath) {
            return rawUrl;
        }

        if (!serviceUrl) {
            return rawUrl;
        }

        return `${serviceUrl}${normalizedPath}`;
    };

    const loadFiles = async (
        query: string,
        category: string,
        sortBy: SortBy = SortBy.CreatedAt,
        order: Order = Order.Desc,
    ) => {
        setIsLoading(true);
        setErrorMessage('');
        try {
            const loaded = await fileLibraryService.listFilesAsync('', sortBy, order, category);
            setAllLibraryFiles(loaded);
            setFiles(filterFilesByQuery(loaded, query));
        } catch (e) {
            setErrorMessage('Dateien konnten nicht geladen werden.');
        } finally {
            setIsLoading(false);
        }
    };

    const reloadCurrentFiles = async () => {
        await loadFiles(searchQuery, '', SortBy.CreatedAt, Order.Desc);
    };

    const libraryItems: FileLibraryListItem[] = files.map((file) => ({
        _id: file.id,
        title: formatDate(file.createdAt),
        description: `Grösse: ${formatBytes(file.size)}`,
        fileName: file.fileName,
        displayPath: getFileDisplayPath(file),
        createdAt: file.createdAt,
        size: file.size,
        thumbnailUrl: imageTypes.includes(file.mimeType) ? resolveFilePublicUrl(file) : undefined,
        item: file,
    }));

    const renderLibraryCard = (item: FileLibraryListItem) => {
        return (
            <div className="react-media-library__file-library-card">
                {item.thumbnailUrl && (
                    <img
                        className="react-media-library__file-library-card__image"
                        src={item.thumbnailUrl}
                        alt={item.fileName}
                    />
                )}
                <div className="react-media-library__file-library-card__path">{item.displayPath ?? item.fileName}</div>
            </div>
        );
    };

    const selectItems = (items: Array<FileLibraryListItem>) => {
        const first = items[0];
        if (!first?.item) {
            return;
        }
        setSelectedImage(first.item as IStoredFileDto);
        setIsLibraryOpen(false);
    };

    const deleteItems = async (items: Array<FileLibraryListItem>) => {
        if (items.length === 0) {
            return;
        }

        const selectedFiles = items
            .map((item) => item.item as IStoredFileDto | undefined)
            .filter((file): file is IStoredFileDto => !!file?.id);

        const deletableFiles = selectedFiles.filter((file) => !isPublicRepoFile(file));
        const skippedFiles = selectedFiles.filter((file) => isPublicRepoFile(file));

        if (deletableFiles.length === 0) {
            setErrorMessage('Dateien aus public sind im Repository und können nicht gelöscht werden.');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');
        try {
            for (const file of deletableFiles) {
                await fileLibraryService.deleteFileAsync(file.id);
            }

            await reloadCurrentFiles();

            if (skippedFiles.length > 0) {
                setErrorMessage('Einige Dateien wurden übersprungen: public-Dateien sind nicht löschbar.');
            }
        } catch (e) {
            setErrorMessage('Datei konnte nicht gelöscht werden.');
        } finally {
            setIsLoading(false);
        }
    };

    const uploadMedia = async (file: File): Promise<boolean> => {
        setIsLoading(true);
        setErrorMessage('');
        try {
            await fileLibraryService.uploadFileAsync(file, file.name, '', 'images');
            return true;
        } catch (e) {
            setErrorMessage('Upload fehlgeschlagen.');
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const importPublicMedia = async () => {
        if (!isAdmin) {
            setErrorMessage('Nur Admins dürfen öffentliche Medien importieren.');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');
        setImportSuccessMessage('');
        try {
            const result = await fileLibraryService.importPublicMediaReferencesAsync();
            setImportSuccessMessage(
                `Import abgeschlossen: ${result.createdReferenceCount} neu, ${result.existingReferenceCount} bereits vorhanden.`,
            );
            await reloadCurrentFiles();
        } catch (e) {
            setErrorMessage('Import der öffentlichen Medien fehlgeschlagen.');
        } finally {
            setIsLoading(false);
        }
    };

    const searchImages = async () => {
        if (allLibraryFiles.length > 0) {
            setFiles(filterFilesByQuery(allLibraryFiles, searchQuery));
            return;
        }

        await loadFiles(searchQuery, '', SortBy.CreatedAt, Order.Desc);
    };

    const renderLibraryTopBar = () => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    mb: 2,
                    alignItems: 'center',
                    pl: 1,
                    pr: 2,
                    width: '100%',
                    boxSizing: 'border-box',
                }}
            >
                <TextField
                    size="small"
                    label="Bilder suchen"
                    placeholder="Dateiname, Titel, Kommentar"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    onKeyDown={async (event) => {
                        if (event.key === 'Enter') {
                            await searchImages();
                        }
                    }}
                    sx={{ minWidth: 240, flex: '1 1 280px' }}
                />
                <Button
                    variant="outlined"
                    onClick={searchImages}
                    sx={{
                        ml: { xs: 0, sm: 'auto' },
                        alignSelf: { xs: 'stretch', sm: 'auto' },
                    }}
                >
                    Suche anwenden
                </Button>
            </Box>
        );
    };

    useEffect(() => {
        loadFiles('', '', SortBy.CreatedAt, Order.Desc);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!isAdmin) {
        return (
            <MainContent>
                <div style={{ ...actionContainerStyle, textAlign: 'left', color: 'black' }}>Kein Zugriff.</div>
            </MainContent>
        );
    }

    return (
        <MainContent>
            <Box style={{ ...actionContainerStyle, textAlign: 'left' }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                    Bilder und File Bibliothek
                </Typography>

                <Button variant="contained" onClick={() => setIsLibraryOpen(true)} disabled={isLoading || !isAdmin}>
                    Files und Bilder suchen
                </Button>
                <Button variant="outlined" sx={{ ml: 1 }} onClick={importPublicMedia} disabled={isLoading || !isAdmin}>
                    Öffentliche Medien importieren
                </Button>

                <ReactMediaLibrary
                    isOpen={isLibraryOpen}
                    onClose={() => setIsLibraryOpen(false)}
                    modalTitle="SwissTexMusic Medien"
                    fileLibraryList={libraryItems}
                    sortProperty="createdAt"
                    sortAscending={false}
                    libraryCardComponent={renderLibraryCard}
                    filesSelectCallback={selectItems}
                    multiSelect={false}
                    fileUploadCallback={uploadMedia}
                    topBarComponent={renderLibraryTopBar}
                    finishUploadCallback={async () => {
                        await reloadCurrentFiles();
                    }}
                    acceptedTypes={acceptedLibraryTypes}
                />

                {selectedImage && (
                    <Card sx={{ mt: 3, maxWidth: 460 }}>
                        {imageTypes.includes(selectedImage.mimeType) && (
                            <CardMedia
                                component="img"
                                height="240"
                                image={resolveFilePublicUrl(selectedImage)}
                                alt={selectedImage.title || selectedImage.fileName}
                                sx={{ cursor: 'zoom-in' }}
                                onClick={() => setIsImagePreviewOpen(true)}
                            />
                        )}
                        <CardContent>
                            <Typography variant="h6">Ausgewähltes File</Typography>
                            <Typography variant="body2">{selectedImage.title || selectedImage.originalName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {getFileDisplayPath(selectedImage)}
                            </Typography>
                            {isManagedLibraryFile(selectedImage) && (
                                <Box sx={{ mt: 1.5 }}>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        size="small"
                                        disabled={isLoading}
                                        onClick={async () => {
                                            await deleteItems([{ _id: selectedImage.id, item: selectedImage }]);
                                            setSelectedImage(null);
                                        }}
                                    >
                                        Datei löschen
                                    </Button>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                )}

                {errorMessage && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {errorMessage}
                    </Alert>
                )}

                {importSuccessMessage && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        {importSuccessMessage}
                    </Alert>
                )}
            </Box>

            <Dialog
                open={isImagePreviewOpen && !!selectedImage && imageTypes.includes(selectedImage.mimeType)}
                onClose={() => setIsImagePreviewOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogContent sx={{ p: 1.5 }}>
                    {selectedImage && imageTypes.includes(selectedImage.mimeType) && (
                        <Box
                            component="img"
                            src={resolveFilePublicUrl(selectedImage)}
                            alt={selectedImage.title || selectedImage.fileName}
                            sx={{ width: '100%', height: 'auto', maxHeight: '85vh', objectFit: 'contain' }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <Snackbar
                open={!!selectedImage}
                autoHideDuration={3500}
                onClose={() => setSelectedImage(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity="success" onClose={() => setSelectedImage(null)}>
                    File ausgewählt: {selectedImage ? resolveFilePublicUrl(selectedImage) : ''}
                </Alert>
            </Snackbar>
        </MainContent>
    );
};
